import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../../../context/AuthContext';
import { useClientMessagingWebSocket } from '../../../../hooks/useClientMessagingWebSocket';
import { useAppointmentsListQuery } from '../../../../services/GLOWClient';
import { messagingService } from '../../../../services/messaging.service';
import { theme } from '../../../../theme';
import ClientChatConversationList from './ClientChatConversationList';
import { useClientChat } from './ClientChatContext';
import ClientChatSalonSearch from './ClientChatSalonSearch';
import ClientChatStarter from './ClientChatStarter';
import ClientChatThread from './ClientChatThread';

export const CLIENT_CHAT_POSITION = {
  launcher: {
    right: { xs: '32px', sm: '40px' },
    bottom: { xs: '32px', sm: '40px' },
  },
  panel: {
    right: { xs: '32px', sm: '40px' },
    bottom: { xs: '104px', sm: '120px' },
  },
};

const isSupportedRoute = (pathname) => (
  !/^\/client\/messages\/?$/.test(pathname)
  && (
    /^\/client(?:\/|$)/.test(pathname)
    || /^\/salon\/[^/]+\/?$/.test(pathname)
  )
);

const getSalonId = (salon) => (
  salon?.salon_id
  ?? salon?.id
  ?? salon
);

const pickRecentOpenConversation = (items, salonId) => (
  [...items]
    .filter((item) => (
      item.status === 'open'
      && Number(getSalonId(item.salon)) === Number(salonId)
    ))
    .sort((left, right) => (
      new Date(right.last_message_at || right.updated_at || 0)
      - new Date(left.last_message_at || left.updated_at || 0)
    ))[0] || null
);

const ClientChatWidget = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const {
    isOpen,
    contextSalon,
    openChat,
    openForSalon,
    closeChat,
    requestedSalon,
    selectedConversationId,
    selectConversation,
    clearRequestedSalon,
  } = useClientChat();
  const [conversations, setConversations] = useState([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [view, setView] = useState('list');
  const [listQuery, setListQuery] = useState('');
  const [returnToList, setReturnToList] = useState(false);
  const [error, setError] = useState('');
  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const supportedRoute = isSupportedRoute(pathname);
  const { data: appointmentsData = [] } = useAppointmentsListQuery(
    {},
    { skip: user?.role !== 'client' || !isOpen },
  );

  useEffect(() => {
    if (user?.role !== 'client' || !supportedRoute) return undefined;
    let active = true;
    setConversationsLoaded(false);
    setError('');
    messagingService.listClientConversations()
      .then((response) => {
        if (!active) return;
        setConversations(
          Array.isArray(response.data)
            ? response.data
            : response.data?.results || [],
        );
      })
      .catch(() => {
        if (active) {
          setError('Impossible de charger les discussions.');
        }
      })
      .finally(() => {
        if (active) setConversationsLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [supportedRoute, user?.role]);

  const onSocketMessage = useCallback((event) => {
    if (event.type !== 'new_message' || !event.message) return;
    const incoming = event.message;
    const isSelected = (
      isOpen
      && Number(selectedConversationId)
        === Number(incoming.conversation_id)
    );

    setConversations((items) => items.map((item) => (
      Number(item.id) === Number(incoming.conversation_id)
        ? {
          ...item,
          last_message: incoming,
          last_message_at: incoming.created_at || item.last_message_at,
          unread_count: (
            isSelected || incoming.sender_type === 'client'
              ? 0
              : Number(item.unread_count || 0) + 1
          ),
        }
        : item
    )));
    setDetail((current) => {
      if (
        !current
        || Number(current.id) !== Number(incoming.conversation_id)
        || current.messages?.some(
          (item) => Number(item.id) === Number(incoming.id),
        )
      ) {
        return current;
      }
      return {
        ...current,
        messages: [...(current.messages || []), incoming],
      };
    });

    if (isSelected && incoming.sender_type !== 'client') {
      messagingService.markClientConversationRead(
        incoming.conversation_id,
      ).catch(() => {});
    }
  }, [isOpen, selectedConversationId]);

  const { sendJson } = useClientMessagingWebSocket({
    token: user?.token || localStorage.getItem('access_token'),
    enabled: user?.role === 'client' && supportedRoute,
    onMessage: onSocketMessage,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (!requestedSalon) {
      if (!selectedConversationId) setView('list');
      return;
    }
    if (!conversationsLoaded) {
      setView('loading');
      return;
    }
    if (requestedSalon.client_chat_enabled !== true) {
      setView('list');
      return;
    }
    const existing = pickRecentOpenConversation(
      conversations,
      getSalonId(requestedSalon),
    );
    if (existing) {
      selectConversation(existing.id);
      setView('thread');
      return;
    }
    setView('starter');
  }, [
    conversations,
    conversationsLoaded,
    isOpen,
    requestedSalon,
    selectConversation,
    selectedConversationId,
  ]);

  useEffect(() => {
    if (!isOpen || !selectedConversationId) return undefined;
    let active = true;
    const conversationId = selectedConversationId;
    setLoadingDetail(true);
    setError('');
    setView('thread');
    messagingService.retrieveClientConversation(conversationId)
      .then((response) => {
        if (!active) return;
        setDetail(response.data);
        setConversations((items) => items.map((item) => (
          Number(item.id) === Number(conversationId)
            ? { ...item, ...response.data, unread_count: 0 }
            : item
        )));
        messagingService.markClientConversationRead(
          conversationId,
        ).catch(() => {});
        sendJson({
          action: 'join_conversation',
          conversation_id: conversationId,
        });
      })
      .catch(() => {
        if (active) {
          setError('Impossible de charger la discussion.');
        }
      })
      .finally(() => {
        if (active) setLoadingDetail(false);
      });

    return () => {
      active = false;
      sendJson({
        action: 'leave_conversation',
        conversation_id: conversationId,
      });
    };
  }, [isOpen, selectedConversationId, sendJson]);

  const unreadCount = useMemo(() => conversations.reduce(
    (total, item) => total + Number(item.unread_count || 0),
    0,
  ), [conversations]);
  const canReturnToList = returnToList || Boolean(requestedSalon);

  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  if (user?.role !== 'client' || !supportedRoute) {
    return null;
  }

  const handleOpen = () => {
    if (contextSalon?.client_chat_enabled === true) {
      setReturnToList(false);
      openForSalon(contextSalon);
      return;
    }
    openChat();
  };

  const handleClose = () => {
    closeChat();
    const restoreFocus = () => launcherRef.current?.focus();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restoreFocus);
    } else {
      window.setTimeout(restoreFocus, 0);
    }
  };

  const handlePanelKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose();
    }
  };

  const returnToConversationList = () => {
    setDetail(null);
    selectConversation(null);
    clearRequestedSalon();
    setReturnToList(false);
    setView('list');
  };

  const selectFromList = (conversation) => {
    setReturnToList(true);
    clearRequestedSalon();
    selectConversation(conversation.id);
    setView('thread');
  };

  const chooseSalon = (salon) => {
    const salonId = getSalonId(salon);
    const existing = pickRecentOpenConversation(conversations, salonId);
    setReturnToList(true);
    if (existing) {
      clearRequestedSalon();
      selectConversation(existing.id);
      setView('thread');
      return;
    }
    openForSalon({ ...salon, salon_id: salonId });
    setView('starter');
  };

  return (
    <>
      {isOpen && (
        <Paper
          ref={panelRef}
          role="dialog"
          aria-label="Messagerie cliente"
          aria-labelledby="client-chat-title"
          aria-modal="false"
          tabIndex={-1}
          onKeyDown={handlePanelKeyDown}
          elevation={12}
          sx={{
            position: 'fixed',
            zIndex: 1399,
            display: 'flex',
            flexDirection: 'column',
            right: CLIENT_CHAT_POSITION.panel.right,
            bottom: CLIENT_CHAT_POSITION.panel.bottom,
            width: { xs: 'calc(100vw - 32px)', sm: 420 },
            maxWidth: 'calc(100vw - 32px)',
            height: {
              xs: 'calc(100dvh - 112px)',
              sm: 'min(650px, calc(100vh - 128px))',
            },
            borderRadius: {
              xs: '22px 22px 16px 16px',
              sm: '22px',
            },
            overflow: 'hidden',
            backgroundColor: theme.colors.secondary[50],
            border: `1px solid ${theme.colors.primary[100]}`,
            boxShadow: '0 24px 60px rgba(67, 44, 23, 0.22)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              flexShrink: 0,
              borderBottom: `1px solid ${theme.colors.primary[100]}`,
            }}
          >
            {view === 'starter' && canReturnToList && (
              <IconButton
                aria-label="Retour aux discussions"
                onClick={returnToConversationList}
                size="small"
                sx={{ mr: 0.5, color: theme.colors.primary[950] }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                id="client-chat-title"
                noWrap
                sx={{ fontWeight: 800 }}
              >
                {
                  detail?.salon?.name
                  || requestedSalon?.name
                  || (view === 'search'
                    ? 'Nouvelle discussion'
                    : 'Messagerie cliente')
                }
              </Typography>
              {(detail?.salon || requestedSalon) && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.colors.grey[600] }}
                >
                  Messagerie du salon
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label="Fermer la messagerie"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            {view === 'loading' && (
              <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}>
                <CircularProgress
                  size={28}
                  sx={{ color: theme.colors.primary[700] }}
                />
              </Box>
            )}
            {view === 'starter' && requestedSalon && (
              <ClientChatStarter
                salon={requestedSalon}
                appointments={
                  Array.isArray(appointmentsData)
                    ? appointmentsData
                    : appointmentsData?.results || []
                }
                onCreated={(conversation) => {
                  setConversations((items) => [
                    conversation,
                    ...items.filter(
                      (item) => Number(item.id) !== Number(conversation.id),
                    ),
                  ]);
                  selectConversation(conversation.id);
                  setView('thread');
                }}
              />
            )}
            {view === 'search' && (
              <ClientChatSalonSearch
                onSelectSalon={chooseSalon}
                onBack={() => setView('list')}
              />
            )}
            {view === 'thread' && (
              loadingDetail || !detail ? (
                <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}>
                  {error ? (
                    <Typography
                      sx={{ px: 3, textAlign: 'center' }}
                      color="text.secondary"
                    >
                      {error}
                    </Typography>
                  ) : (
                    <CircularProgress
                      size={28}
                      sx={{ color: theme.colors.primary[700] }}
                    />
                  )}
                </Box>
              ) : (
                <ClientChatThread
                  conversation={detail}
                  onConversationChange={(conversation) => {
                    setDetail(conversation);
                    setConversations((items) => items.map((item) => (
                      Number(item.id) === Number(conversation.id)
                        ? {
                          ...item,
                          ...conversation,
                          unread_count: 0,
                        }
                        : item
                    )));
                  }}
                  onBack={canReturnToList
                    ? returnToConversationList
                    : undefined}
                />
              )
            )}
            {view === 'list' && (
              !conversationsLoaded ? (
                <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}>
                  <CircularProgress
                    size={28}
                    sx={{ color: theme.colors.primary[700] }}
                  />
                </Box>
              ) : (
                <>
                  {error && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ px: 2, pt: 1.5 }}
                    >
                      {error}
                    </Typography>
                  )}
                  <ClientChatConversationList
                    conversations={conversations}
                    query={listQuery}
                    onQueryChange={setListQuery}
                    onSelect={selectFromList}
                    onNewConversation={() => setView('search')}
                  />
                </>
              )
            )}
          </Box>
        </Paper>
      )}

      <IconButton
        ref={launcherRef}
        aria-label={
          unreadCount
            ? `Ouvrir la messagerie, ${unreadCount} message${unreadCount > 1 ? 's' : ''} non lu${unreadCount > 1 ? 's' : ''}`
            : 'Ouvrir la messagerie'
        }
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          zIndex: 1400,
          right: CLIENT_CHAT_POSITION.launcher.right,
          bottom: CLIENT_CHAT_POSITION.launcher.bottom,
          width: 58,
          height: 58,
          backgroundColor: theme.colors.primary[800],
          color: 'white',
          boxShadow: '0 12px 30px rgba(107, 56, 26, 0.32)',
          '&:hover': {
            backgroundColor: theme.colors.primary[900],
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          invisible={unreadCount === 0}
        >
          <ChatBubbleOutlineIcon />
        </Badge>
      </IconButton>
    </>
  );
};

export default ClientChatWidget;
